import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Input,
  showToast,
} from '@repo/ui';

import { useUpdateClioCredential } from '../../queries/useClioCredentialsQuery';
import type { PublicCredentialMetadata } from '../../api/clioCredentialsApi';

interface EditLabelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  credential: PublicCredentialMetadata | null;
}

export function EditLabelDialog({ isOpen, onClose, credential }: EditLabelDialogProps) {
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const updateMutation = useUpdateClioCredential();

  useEffect(() => {
    if (isOpen && credential) {
      setLabel(credential.label);
      setError(null);
    }
  }, [isOpen, credential]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credential) return;
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Label is required');
      return;
    }
    if (trimmed === credential.label) {
      onClose();
      return;
    }
    try {
      await updateMutation.mutateAsync({
        credentialId: credential.credentialId,
        patch: { label: trimmed },
      });
      showToast.success('Label updated');
      onClose();
    } catch {
      // Toast already shown via mutation onError
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="sm">
      <form onSubmit={handleSubmit} className="flex flex-col">
        <DialogHeader>
          <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
            Edit label
          </h2>
        </DialogHeader>
        <DialogBody>
          <Input
            id="label"
            label="Label"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              if (error) setError(null);
            }}
            error={error ?? undefined}
            disabled={updateMutation.isPending}
            fullWidth
            required
          />
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={updateMutation.isPending}
            disabled={updateMutation.isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
